/**
 * HTTP Client Factory (infra-level primitive)
 * Creates Axios instances with cross-cutting interceptors.
 *
 * NOTE:
 * - No domain logic (no authStorage, no redirects, no feature imports).
 * - Authorization is injected via getAccessToken callback from services layer.
 * @module @infra/http
 */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import { getI18nService } from '@infra/i18n';
import { maskUrlForLog } from '@shared/lib';
import type { HttpError } from './http.types';
import { HTTP_HEADERS } from './http.constants';
import {
  CanceledRequestError,
  NetworkError,
  AuthError,
  ForbiddenError,
  NotFoundError,
  ValidationError,
  ServerError,
  TimeoutError,
} from './http.errors';

/**
 * Options for creating an HTTP client instance
 */
export type CreateHttpClientOptions = {
  /** Base URL for all requests */
  baseURL: string;
  /** Optional timeout for requests in milliseconds */
  timeout?: number;
  /** Optional function to retrieve access token for authentication */
  getAccessToken?: () => string | null;
};

interface ErrorResponseData {
  message?: string;
  errors?: Record<string, string[]>;
}

function normalizeError(error: AxiosError): HttpError {
  let httpError: HttpError;

  // Handle network errors (no response)
  if (!error.response) {
    // An abort has no response either, but it is not a failure — react-query
    // cancels in-flight fetches on unmount and on every superseded refetch.
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      httpError = new CanceledRequestError();
      httpError._requestConfig = error.config;
      return httpError;
    }
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      httpError = new TimeoutError();
    } else {
      httpError = new NetworkError(undefined, error);
    }
    // Preserve original request config for retry scenarios (e.g. 401 refresh)
    httpError._requestConfig = error.config;
    return httpError;
  }

  const status = error.response.status;
  const data = error.response.data as ErrorResponseData | undefined;
  const message = data?.message || error.response.statusText || error.message || 'Unknown error';

  // Classify errors by status code
  switch (status) {
    case 401:
      httpError = new AuthError(message, status);
      break;

    case 403:
      httpError = new ForbiddenError(message, status);
      break;

    case 404:
      httpError = new NotFoundError(message, error.config?.url);
      break;

    case 408:
      httpError = new TimeoutError(message);
      break;

    case 422:
      httpError = new ValidationError(message, data?.errors);
      break;

    case 500:
    case 502:
    case 503:
    case 504:
      httpError = new ServerError(message, status);
      break;

    default: {
      // Generic HTTP error for other status codes
      const genericError = new Error(message) as HttpError;
      genericError.status = status;
      genericError.statusText = error.response.statusText;
      genericError.code = error.code;
      genericError.data = data;
      httpError = genericError;
    }
  }

  // Expose the response body so callers can read app codes (e.g. SESSION_SUPERSEDED).
  httpError.data = data;
  // Preserve original request config for retry scenarios (e.g. 401 refresh)
  httpError._requestConfig = error.config;
  return httpError;
}

/**
 * Creates a configured Axios HTTP client instance with interceptors
 *
 * Features:
 * - Automatic token injection via Authorization header
 * - Language header injection from i18n service
 * - Request/response logging in development
 * - Error normalization with specific error types
 *
 * @param options - Configuration options for the HTTP client
 * @returns Configured Axios instance
 *
 * @example
 * ```ts
 * const client = createHttpClient({
 *   baseURL: 'https://api.example.com',
 *   getAccessToken: () => localStorage.getItem('token'),
 * });
 * ```
 */
export function createHttpClient(options: CreateHttpClientOptions): AxiosInstance {
  const instance = axios.create({
    baseURL: options.baseURL,
    timeout: options.timeout || 30000,
  });

  // Request interceptor: attach token (if provided) + language header
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = options.getAccessToken?.();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      try {
        const i18n = getI18nService();
        const language = i18n.getLanguage();
        config.headers[HTTP_HEADERS.ACCEPT_LANGUAGE] = language;
      } catch {
        // i18n may not be initialised yet – ignore silently
      }

      return config;
    },
    (error) => {
      if (import.meta.env.DEV) {
        console.error('HTTP request error:', error);
      }
      return Promise.reject(error);
    }
  );

  // Response interceptor: logging + generic error normalization
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      const httpError = normalizeError(error);

      // A canceled request is routine bookkeeping, not something to report.
      if (import.meta.env.DEV && !(httpError instanceof CanceledRequestError)) {
        const sanitizedUrl = maskUrlForLog(error.config?.url);
        console.error(`✗ ${error.config?.method?.toUpperCase()} ${sanitizedUrl}`, httpError);
      }

      return Promise.reject(httpError);
    }
  );

  return instance;
}

export type { AxiosInstance, AxiosError };
